import type { ApiRequestHelpers } from "./useHttpClient";
import type {
  FilterByQuery,
  IncludeQuery,
  Location,
  LocationAnalytics,
  LocationColorsResponse,
  LocationHealthDashboard,
  LocationIdentifier,
  LocationReference,
  CreateLocationPayload,
  PaginationQuery,
  Response,
  SortByQuery,
  UpdateLocationPayload,
  ZorgdomeinActivation,
} from "~/types/api";
import type { EmailPreferenceType } from "~/constants/emailCommunicationTypes";

export function createLocationApi({
  httpClient,
  getRequest,
  postRequest,
  patchRequest,
  deleteRequest,
  getPaginationRequest,
}: ApiRequestHelpers) {
  /** Fetches a paginated list of locations. */
  const locations = (
    pagination: PaginationQuery,
    sortBy?: SortByQuery,
    includes?: IncludeQuery,
    filterBy?: FilterByQuery,
  ) => {
    return getPaginationRequest<LocationReference>("/locations", pagination, sortBy, includes, filterBy);
  };

  /** Fetches a single location by ID with optional includes. */
  const location = (locationId: LocationIdentifier, options?: { include?: string }) =>
    getRequest<Response<Location>>(
      `/locations/${locationId}`,
      options?.include ? { params: { include: options.include } } : {},
    );

  /** Creates a new location. */
  const createLocation = (data: CreateLocationPayload) => postRequest<Response<LocationReference>>("/locations", data);

  /** Updates an existing location. */
  const updateLocation = (locationId: LocationIdentifier, data: UpdateLocationPayload) =>
    patchRequest<Response<Location>>(`/locations/${locationId}`, data);

  /** Assigns a location to an organization. */
  const assignLocationToOrganization = (locationId: LocationIdentifier, organizationId: string) =>
    patchRequest<Response<Location>>(`/locations/${locationId}/assign-organization`, {
      organization_id: organizationId,
    });

  /** Updates email notification preferences for a location. */
  const updateLocationEmailPreferences = (
    locationId: LocationIdentifier,
    data: Partial<Record<EmailPreferenceType, boolean>>,
  ) => patchRequest<Response<Location>>(`/locations/${locationId}/email-preferences`, data);

  /** Updates government connection settings for a location. */
  const updateGovConnections = (
    locationId: LocationIdentifier,
    data: {
      sbvz_check: boolean | null;
      cov_check: boolean | null;
      vecozo_declaration: boolean | null;
    },
  ) => postRequest<Response<Location>>(`/locations/${locationId}/gov-connections`, data);

  /** Fetches the logo image for a location as raw binary data. */
  const getLocationLogo = (locationId: LocationIdentifier) => {
    return httpClient
      .get(`/locations/${locationId}/logo`, { responseType: "arraybuffer" })
      .then((response) => response.data);
  };

  /** Uploads a new logo for a location. */
  const updateLocationLogo = (locationId: LocationIdentifier, data: FormData) =>
    postRequest<Response<Location>>(`/locations/${locationId}/logo`, data);

  /** Deletes the logo for a location. */
  const deleteLocationLogo = (locationId: LocationIdentifier) =>
    deleteRequest<Response<Location>>(`/locations/${locationId}/logo`);

  /** Fetches analytics data for a location within a date range. */
  const getLocationAnalytics = (locationId: number, params: { start_date: string; end_date: string }) =>
    getRequest<{ data: LocationAnalytics }>(`/locations/${locationId}/analytics`, { params }).then(
      (response) => response.data,
    );

  /** Fetches the available color options for locations. */
  const getLocationColors = () => getRequest<LocationColorsResponse>("/locations/colors");

  /** Fetches health dashboard metrics for a location. */
  const getLocationHealthDashboard = (locationId: LocationIdentifier) =>
    getRequest<Response<LocationHealthDashboard>>(`/locations/${locationId}/health`).then((response) => response.data);

  const zorgdomeinActivation = (locationId: LocationIdentifier) =>
    getRequest<Response<ZorgdomeinActivation>>(`/locations/${locationId}/zorgdomein-activation`);

  return {
    locations,
    location,
    createLocation,
    updateLocation,
    assignLocationToOrganization,
    updateLocationEmailPreferences,
    updateGovConnections,
    getLocationLogo,
    updateLocationLogo,
    deleteLocationLogo,
    getLocationAnalytics,
    getLocationColors,
    getLocationHealthDashboard,
    zorgdomeinActivation,
  };
}

export type LocationApi = ReturnType<typeof createLocationApi>;
